import { useLayoutEffect, useState } from 'react';

import { useThrottledCallback } from './useThrottledCallback';

import type { RefObject } from 'react';

export function useBoundingRect<T extends Element>(
  ref: RefObject<T>,
  deps: unknown[] = [],
): DOMRect {
  const [rect, setRect] = useState<DOMRect>(() => getRect(ref.current));

  const handleResize = useThrottledCallback(() => {
    if (ref.current) {
      setRect(getRect(ref.current)); // Update client rect
    }
  }, 200);

  // recalculate bounding rect on deps change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(handleResize, deps);

  // recalculate bounding rect on resize
  useLayoutEffect(() => {
    window.addEventListener('resize', handleResize); // Update to use resizeObserver when support
    // is better
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return rect;
}

function getRect<T extends Element | null>(element?: T): DOMRect {
  if (element) {
    return element.getBoundingClientRect();
  }
  return {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
    x: 0,
    y: 0,
    // eslint-disable-next-line no-console
    toJSON: console.log,
  };
}
